import { useMemo } from 'react';
import {
  ArrowsCounterClockwise,
  DotsThree,
  MapPin,
  PencilSimple,
  TrashSimple,
  Waves,
} from '@phosphor-icons/react';
import { Button, Dropdown, Flex, Table, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { MuteLink } from '@elements/Common';
import { COLOR } from '@styles/theme';
import { CommonConstant, CommonUtility, PalletStatusColors } from '@utility/index';
import { PalletTag, UsedAsStoragePill } from './common';

const { Text } = Typography;

export type PalletRecord = {
  _id: string;
  palletNumber?: string;
  usedAsStorage?: boolean;
  status?: string;
  warehouseId?: string;
  zoneId?: { _id?: string; code?: string; name?: string; color?: string };
  dimensions?: { length?: number; height?: number; width?: number; weightLimit?: number };
  waves?: { waveId?: { _id?: string; waveNumber?: string } }[];
  embeddedIn?: {
    code?: string;
    zoneId?: { _id?: string };
    rackId?: { _id?: string };
  };
};

type PalletsTableProps = {
  data: PalletRecord[];
  loading: boolean;
  totalData: number;
  filter: { page: number; limit: number; sort: { sortBy: string; sortOrder: string } };
  sort: Record<string, string>;
  pageChanged: (page: number) => void;
  filterChanged: (key: string, value: unknown) => void;
  onSortChange: (sortObject: Record<string, string>) => void;
  selectedById: Record<string, PalletRecord>;
  onSelectRow: (item: PalletRecord, selected: boolean) => void;
  onSelectAllRows: (items: PalletRecord[], selected: boolean) => void;
  editPallet: (item: PalletRecord) => void;
  onDeletePallet: (item: PalletRecord) => void;
  resetPallet: (item: PalletRecord) => void;
};

function toAntSortOrder(sortOrder: string): 'ascend' | 'descend' | null {
  if (sortOrder === CommonConstant.sortDirection.ascending) return 'ascend';
  if (sortOrder === CommonConstant.sortDirection.descending) return 'descend';
  return null;
}

function getRowActionItems(
  record: PalletRecord,
  handlers: Pick<PalletsTableProps, 'editPallet' | 'onDeletePallet' | 'resetPallet'>,
): MenuProps['items'] {
  const items: MenuProps['items'] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <PencilSimple size={16} />,
      onClick: () => handlers.editPallet(record),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <TrashSimple size={16} />,
      danger: true,
      onClick: () => handlers.onDeletePallet(record),
    },
  ];

  if (record.status !== 'available') {
    items.push({
      key: 'reset',
      label: 'Reset',
      icon: <ArrowsCounterClockwise size={16} />,
      danger: true,
      onClick: () => handlers.resetPallet(record),
    });
  }

  return items;
}

export function PalletsTable({
  data,
  loading,
  totalData,
  filter,
  sort,
  pageChanged,
  filterChanged,
  onSortChange,
  selectedById,
  onSelectRow,
  onSelectAllRows,
  editPallet,
  onDeletePallet,
  resetPallet,
}: PalletsTableProps) {
  const activeSortField = filter.sort.sortBy;
  const activeAntSortOrder = toAntSortOrder(filter.sort.sortOrder);

  const columns: ColumnsType<PalletRecord> = useMemo(
    () => [
      {
        title: 'Pallet Number',
        key: 'palletNumber',
        dataIndex: 'palletNumber',
        sorter: true,
        sortOrder: activeSortField === 'palletNumber' ? activeAntSortOrder : null,
        render: (_, record) => (
          <Flex align="center" gap={8} wrap="nowrap">
            <span>{record.palletNumber}</span>
            <UsedAsStoragePill usedAsStorage={record.usedAsStorage} />
          </Flex>
        ),
      },
      {
        title: 'Assigned Zone',
        key: 'zone',
        render: (_, record) =>
          record.zoneId?.code ? (
            <PalletTag color={record.zoneId?.color}>{record.zoneId?.name}</PalletTag>
          ) : (
            <Text type="secondary">Not Set</Text>
          ),
      },
      {
        title: 'Size',
        key: 'size',
        render: (_, record) =>
          record.dimensions?.length ? (
            <span>
              {record.dimensions.length}
              <Text type="secondary" className="mx-1">
                x
              </Text>
              {record.dimensions.height}
              <Text type="secondary" className="mx-1">
                x
              </Text>
              {record.dimensions.width}
              <Text type="secondary" className="ml-1">
                in.
              </Text>
            </span>
          ) : (
            <Text type="secondary">Not Set</Text>
          ),
      },
      {
        title: 'Weight Limit',
        key: 'weight_limit',
        render: (_, record) =>
          record.dimensions?.weightLimit ? (
            record.dimensions.weightLimit
          ) : (
            <Text type="secondary">Not Set</Text>
          ),
      },
      {
        title: 'Status',
        key: 'status',
        dataIndex: 'status',
        sorter: true,
        sortOrder: activeSortField === 'status' ? activeAntSortOrder : null,
        render: (_, record) => (
          <Flex align="center" gap={8} wrap="wrap">
            <PalletTag color={PalletStatusColors[record.status ?? '']}>
              {CommonUtility.removeUnderscoreAndCapitalize(record.status ?? '')}
            </PalletTag>
            {record?.waves?.[0]?.waveId?.waveNumber ? (
              <MuteLink
                to={`/app/fulfill/waves/${record.waves[0].waveId?._id}`}
                className="text-nowrap d-flex align-items-center"
                target="_blank"
              >
                <Waves size={18} className="mr-1" />
                {record.waves[0].waveId?.waveNumber}
              </MuteLink>
            ) : null}
            {CommonUtility.isValidObject(record.embeddedIn) ? (
              <MuteLink
                target="_blank"
                className="text-nowrap d-flex align-items-center"
                to={`/app/settings/warehouse/${record.warehouseId}/zones/${record.embeddedIn?.zoneId?._id}/racks/${record.embeddedIn?.rackId?._id}`}
              >
                <MapPin size={18} className="mr-1" />
                {record.embeddedIn?.code}
              </MuteLink>
            ) : null}
          </Flex>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 48,
        fixed: 'right',
        render: (_, record) => (
          <Dropdown
            menu={{
              items: getRowActionItems(record, {
                editPallet,
                onDeletePallet,
                resetPallet,
              }),
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<DotsThree size={18} weight="bold" />}
              aria-label="Row actions"
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [activeSortField, activeAntSortOrder, editPallet, onDeletePallet, resetPallet],
  );

  const selectedRowKeys = useMemo(() => Object.keys(selectedById), [selectedById]);

  const pagination: TablePaginationConfig = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
    showTotal: total => `${total} pallets`,
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <Table<PalletRecord>
      rowKey="_id"
      columns={columns}
      dataSource={data}
      loading={loading}
      size="middle"
      tableLayout="fixed"
      pagination={pagination}
      rowSelection={{
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onSelect: (record, selected) => onSelectRow(record, selected),
        onSelectAll: (selected, _rows, changeRows) =>
          onSelectAllRows(changeRows, selected),
      }}
      onChange={(_pagination, _filters, sorter, extra) => {
        if (extra.action !== 'sort') return;

        const single = Array.isArray(sorter) ? sorter[0] : sorter;
        if (single?.field && single.order) {
          onSortChange({
            [String(single.field)]:
              single.order === 'ascend'
                ? CommonConstant.sortDirection.ascending
                : CommonConstant.sortDirection.descending,
          });
        }
      }}
      style={{
        background: COLOR.white,
        borderRadius: 6,
        border: `1px solid ${COLOR.border}`,
        overflow: 'hidden',
      }}
    />
  );
}
